import { useEffect, useMemo, useState } from 'react'

import { getInventoryPanelCounts, type InventoryFilters } from '@/lib/api/client/inventory'
import { MarketplaceBindingSchema, MissingTaxonomyFacetSchema, type ProviderSlot } from '@/lib/api/schemas/inventory.schema'
import type { PanelCounts } from '@/components/warehouse/WarehouseFilters'
import {
  STATUS_SCOPES,
  STOREFRONT_CODES,
  panelCountKey,
  statusPanelCountKey,
  storefrontPanelCountKey,
  warehousePresetsFor,
} from '@/lib/warehouse/url-state'
import type { ViewPresetKey } from '@/lib/views/presets'

const DEBOUNCE_MS = 300
const ORDER_PROGRAMS = ['DBS', 'FBS'] as const
const cache = new Map<string, Record<string, number>>()

/** Every value of the panel that shows a count, as the server's `panelCount` keys. */
export function warehousePanelCountKeys(view: ViewPresetKey, providerSlots: readonly ProviderSlot[] | null): string[] {
  const keys = warehousePresetsFor(view).flatMap((preset) => panelCountKey(preset.filters) ?? [])
  const listed = STOREFRONT_CODES.filter((code) => (providerSlots ?? []).some((slot) => slot.providerCode === code && slot.connectionState === 'connected' && slot.placementObserved))
  keys.push(...listed.flatMap((code) => [storefrontPanelCountKey(code, 'present'), storefrontPanelCountKey(code, 'absent')]))
  keys.push(...STATUS_SCOPES.map(statusPanelCountKey))
  if (view === 'orders') {
    keys.push(...ORDER_PROGRAMS.flatMap((program) => panelCountKey({ orderProgram: [program] }) ?? []))
  } else {
    keys.push(...MarketplaceBindingSchema.options.flatMap((binding) => panelCountKey({ binding }) ?? []))
    keys.push(...MissingTaxonomyFacetSchema.options.flatMap((missingTaxonomy) => panelCountKey({ missingTaxonomy }) ?? []))
  }
  return [...new Set(keys)]
}

/**
 * The count beside each value of the open panel: what the list would total if it
 * were chosen. All of them come from ONE one-row read of the list route.
 */
export function useWarehousePanelCounts({ open, view, filters, base, providerSlots, refreshToken }: {
  open: boolean
  view: ViewPresetKey
  filters: InventoryFilters
  base: Pick<InventoryFilters, 'lifecycle' | 'withPhotos'>
  providerSlots: readonly ProviderSlot[] | null
  refreshToken: number
}): PanelCounts {
  const keys = useMemo(() => warehousePanelCountKeys(view, providerSlots), [providerSlots, view])
  const query = useMemo<InventoryFilters>(() => ({ ...filters, lifecycle: base.lifecycle, withPhotos: base.withPhotos }), [base.lifecycle, base.withPhotos, filters])
  const cacheKey = JSON.stringify({ query, keys, refreshToken })
  const [counts, setCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    if (!open) {
      setCounts({})
      return undefined
    }
    const known = cache.get(cacheKey)
    if (known !== undefined) {
      setCounts(known)
      return undefined
    }
    setCounts({})
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      getInventoryPanelCounts(query, keys, controller.signal)
        .then((next) => {
          cache.set(cacheKey, next)
          if (!controller.signal.aborted) setCounts(next)
        })
        .catch(() => {
          // A failed read leaves every value without a number rather than with a stale one.
        })
    }, DEBOUNCE_MS)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  // The cache key is the value identity of the query, the keys and the refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cacheKey])

  return open ? counts : {}
}
